import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { archiveTasks } from '../api/taskApi';
import { withUndo } from '../utils/undoableAction';
import { showError } from '../utils/toast';
import { useThemeMode } from '../theme/ThemeContext';
import { tokenStorage } from '../app/tokenStorage';

/**
 * Floating bar that appears at the bottom when tasks are selected.
 * Listens for:
 *  - 'bulkSelectionChanged' event with detail: { selectedIds: number[] }
 *  - 'bulkSelectionClear' event to clear selection
 *
 * Dispatches:
 *  - 'bulkActionCompleted' after archive — parent should refresh tasks
 *  - 'bulkSelectionClear' to clear selection
 */
const BulkActionsBar = () => {
    const [selectedIds, setSelectedIds] = useState([]);
    const { themeMode } = useThemeMode();
    const isDark = themeMode === 'dark';

    useEffect(() => {
        const onChange = (e) => {
            const ids = Array.isArray(e.detail?.selectedIds) ? e.detail.selectedIds : [];
            setSelectedIds(ids);
        };
        const onClear = () => setSelectedIds([]);
        window.addEventListener('bulkSelectionChanged', onChange);
        window.addEventListener('bulkSelectionClear', onClear);
        return () => {
            window.removeEventListener('bulkSelectionChanged', onChange);
            window.removeEventListener('bulkSelectionClear', onClear);
        };
    }, []);

    const clearSelection = () => {
        window.dispatchEvent(new CustomEvent('bulkSelectionClear'));
    };

    const handleArchive = () => {
        const ids = [...selectedIds];
        if (ids.length === 0) return;
        clearSelection();
        withUndo({
            message: `${ids.length} task${ids.length > 1 ? 's' : ''} archived`,
            timeout: 5000,
            onCommit: async () => {
                try {
                    await archiveTasks(tokenStorage.getUserId(), ids);
                    window.dispatchEvent(new CustomEvent('bulkActionCompleted'));
                } catch (err) {
                    showError('Failed to archive tasks');
                }
            },
            onUndo: async () => {
                // Since we delayed the commit, undo is just dismissing — nothing to revert server-side
                // But re-fetch to ensure UI is consistent
                window.dispatchEvent(new CustomEvent('bulkActionCompleted'));
            }
        });
    };

    if (selectedIds.length === 0) return null;

    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: { xs: 72, md: 90 },
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: isDark ? '#0e6c52' : '#1f2d3d',
                color: '#fff',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                zIndex: 1300,
                minWidth: 320
            }}
        >
            <Typography sx={{ fontSize: '13px', fontWeight: 600, flex: 1 }}>
                {selectedIds.length} selected
            </Typography>
            <Button
                size="small"
                startIcon={<ArchiveOutlinedIcon />}
                onClick={handleArchive}
                sx={{
                    color: '#fff',
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 600,
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
            >
                Archive
            </Button>
            <IconButton
                size="small"
                onClick={clearSelection}
                sx={{ color: '#fff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
    );
};

export default BulkActionsBar;
